/**
 * Enhanced Create/Edit Form Component for Format Manager
 */

import { useState, useEffect } from 'react';
import SortingGroupingConfig from '../components/SortingGroupingConfig';
import ConditionBuilder from '../components/ConditionBuilder';
import Tooltip from '../components/Tooltip';
import DataGrid from '../components/DataGrid';
import { getPresets, applyPreset } from '../utils/FormattingPresets';

const EnhancedFormatForm = ({
    formData,
    setFormData,
    templates,
    onSubmit,
    onCancel,
    onPreview,
    previewData,
    loading,
    activeSection,
    setActiveSection
}) => {
    const [previousConfig, setPreviousConfig] = useState(null);
    const [appliedPreset, setAppliedPreset] = useState(null);
    const [templateColumns, setTemplateColumns] = useState([]);
    const [templateSampleData, setTemplateSampleData] = useState(null);

    const sections = [
        { id: 'basic', label: 'Basic Info', icon: 'info' },
        { id: 'standardization', label: 'Standardization', icon: 'format' },
        { id: 'sorting', label: 'Sorting & Grouping', icon: 'sort' },
        { id: 'conditional', label: 'Conditional Rules', icon: 'rules' },
        { id: 'cleanup', label: 'Data Cleanup', icon: 'cleanup' },
        { id: 'export', label: 'Export Options', icon: 'reports' },
        { id: 'reports', label: 'Reports', icon: 'reports' },
        { id: 'preview', label: 'Preview', icon: 'preview' }
    ];

    const presets = getPresets();

    // Fetch template columns when template is selected
    useEffect(() => {
        const fetchTemplateDetails = async () => {
            if (!formData.template_id) {
                setTemplateColumns([]);
                setTemplateSampleData(null);
                return;
            }

            try {
                const response = await fetch(`/api/templates/${formData.template_id}`, {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    // Extract columns from template schema or sample data
                    const columns = extractColumnsFromTemplate(data);
                    setTemplateColumns(columns);

                    // Store sample data for preview
                    if (data.sample_data) {
                        setTemplateSampleData(data.sample_data);
                    }
                }
            } catch (err) {
                console.error('Failed to fetch template details:', err);
            }
        };

        fetchTemplateDetails();
    }, [formData.template_id]);

    const extractColumnsFromTemplate = (templateData) => {
        // Try to extract columns from various sources
        if (templateData.schema?.fields) {
            return templateData.schema.fields.map(f => f.name);
        }
        if (templateData.columns) {
            return templateData.columns;
        }
        if (templateData.sample_data && Array.isArray(templateData.sample_data) && templateData.sample_data.length > 0) {
            return Object.keys(templateData.sample_data[0]);
        }
        // Default columns if none found
        return ['value', 'Name', 'Date', 'Amount', 'Status', 'Category'];
    };

    const handleApplyPreset = (presetId) => {
        if (!presetId) return;
        // Store current config for undo
        setPreviousConfig(JSON.parse(JSON.stringify(formData)));
        setAppliedPreset(presetId);
        const updatedData = applyPreset(presetId, formData);
        setFormData(updatedData);
    };

    const handleUndoPreset = () => {
        if (previousConfig) {
            setFormData(previousConfig);
            setPreviousConfig(null);
            setAppliedPreset(null);
        }
    };

    const isBasicInfoComplete = formData.template_id && formData.name?.trim().length > 0;

    const FormatIcon = ({ type, size = 20 }) => {
        const icons = {
            info: (
                <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
            ),
            format: (
                <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14,2 14,8 20,8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
            ),
            rules: (
                <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 12l2 2 4-4" />
                    <circle cx="12" cy="12" r="10" />
                </svg>
            ),
            cleanup: (
                <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 6h18" />
                    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                </svg>
            ),
            reports: (
                <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14,2 14,8 20,8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10,9 9,9 8,9" />
                </svg>
            ),
            preview: (
                <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                </svg>
            ),
            sort: (
                <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 6h18M7 12h10M10 18h4" />
                </svg>
            )
        };
        return icons[type] || icons.info;
    };

    const updateFormData = (path, value) => {
        setFormData(prev => {
            // Use structuredClone for deep copy if available, otherwise fallback to JSON parse/stringify
            const newData = typeof structuredClone === 'function'
                ? structuredClone(prev)
                : JSON.parse(JSON.stringify(prev));

            const keys = path.split('.');
            let current = newData;

            for (let i = 0; i < keys.length - 1; i++) {
                if (!current[keys[i]]) current[keys[i]] = {};
                current = current[keys[i]];
            }

            current[keys[keys.length - 1]] = value;
            return newData;
        });
    };

    const renderBasicSection = () => (
        <div className="form-section active">
            <div className="section-header">
                <FormatIcon type="info" size={24} />
                <div>
                    <h3>Basic Information</h3>
                    <p>Configure the basic settings for your formatting configuration</p>
                </div>
            </div>

            <div className="form-grid">
                <div className="form-group">
                    <Tooltip content="Select the underlying data template this configuration will apply to.">
                        <label className="form-label">
                            <FormatIcon type="format" size={16} />
                            Template
                        </label>
                    </Tooltip>
                    <select
                        id="tutorial-template-select"
                        className="form-input"
                        value={formData.template_id}
                        onChange={(e) => updateFormData('template_id', e.target.value)}
                        required
                    >
                        <option value="">Select a template</option>
                        {templates.map(template => (
                            <option key={template.id} value={template.id}>
                                {template.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="form-group">
                    <Tooltip content="Provide a unique name to identify this formatting configuration.">
                        <label className="form-label">Configuration Name</label>
                    </Tooltip>
                    <input
                        id="tutorial-config-name"
                        type="text"
                        className="form-input"
                        value={formData.name}
                        onChange={(e) => updateFormData('name', e.target.value)}
                        placeholder="e.g., Financial Report Format"
                        required
                    />
                </div>

                <div className="form-group full-width">
                    <Tooltip content="Briefly describe what formatting rules are being applied.">
                        <label className="form-label">Description</label>
                    </Tooltip>
                    <textarea
                        className="form-input"
                        value={formData.description}
                        onChange={(e) => updateFormData('description', e.target.value)}
                        placeholder="Describe what this configuration does..."
                        rows="3"
                    />
                </div>

                <div className="form-group full-width">
                    <Tooltip content="Apply a pre-defined set of rules to quickly set up your configuration.">
                        <label className="form-label">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                                <path d="M2 17l10 5 10-5" />
                                <path d="M2 12l10 5 10-5" />
                            </svg>
                            Quick Start with Preset
                        </label>
                    </Tooltip>
                    <select
                        id="tutorial-presets-select"
                        className="form-input"
                        onChange={(e) => handleApplyPreset(e.target.value)}
                        defaultValue=""
                    >
                        <option value="">Select a preset to apply...</option>
                        {presets.map(preset => (
                            <option key={preset.id} value={preset.id}>
                                {preset.name} - {preset.description}
                            </option>
                        ))}
                    </select>
                    {appliedPreset && previousConfig && (
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={handleUndoPreset}
                            style={{ marginTop: '0.5rem' }}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 10h10a5 5 0 0 1 0 10H9" />
                                <polyline points="7 14 3 10 7 6" />
                            </svg>
                            Undo Preset
                        </button>
                    )}
                    <span className="form-hint">Presets provide a quick way to configure common formatting scenarios</span>
                </div>
            </div>
        </div>
    );
    const renderStandardizationSection = () => (
        <div className="form-section active">
            <div className="section-header">
                <FormatIcon type="format" size={24} />
                <div>
                    <h3>Data Standardization</h3>
                    <p>Configure how different data types should be formatted</p>
                </div>
            </div>

            <div className="standardization-grid">
                <div className="format-card">
                    <div className="format-card-header">
                        <div className="format-card-title">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                <line x1="16" y1="2" x2="16" y2="6" />
                                <line x1="8" y1="2" x2="8" y2="6" />
                                <line x1="3" y1="10" x2="21" y2="10" />
                            </svg>
                            <Tooltip content="Standardize how dates appear across all outputs. Supported formats include ISO 8601, MM/DD/YYYY, and more.">
                                <span>Date Formatting</span>
                            </Tooltip>
                        </div>
                        <label className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={formData.config_data.standardization.dates.enabled}
                                onChange={(e) => updateFormData('config_data.standardization.dates.enabled', e.target.checked)}
                            />
                            <span className="toggle-slider"></span>
                        </label>
                    </div>

                    {formData.config_data.standardization.dates.enabled && (
                        <div className="format-card-content">
                            <div className="form-row">
                                <div className="form-group">
                                    <Tooltip content="Select the pattern for the output date string.">
                                        <label>Output Format</label>
                                    </Tooltip>
                                    <select
                                        className="form-input"
                                        value={formData.config_data.standardization.dates.output_format}
                                        onChange={(e) => updateFormData('config_data.standardization.dates.output_format', e.target.value)}
                                    >
                                        <option value="YYYY-MM-DD">YYYY-MM-DD (2024-01-15)</option>
                                        <option value="MM/DD/YYYY">MM/DD/YYYY (01/15/2024)</option>
                                        <option value="DD/MM/YYYY">DD/MM/YYYY (15/01/2024)</option>
                                        <option value="YYYY-MM-DD HH:mm:ss">YYYY-MM-DD HH:mm:ss</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <Tooltip content="Choose the target timezone for all date values.">
                                        <label>Timezone</label>
                                    </Tooltip>
                                    <select
                                        className="form-input"
                                        value={formData.config_data.standardization.dates.timezone}
                                        onChange={(e) => updateFormData('config_data.standardization.dates.timezone', e.target.value)}
                                    >
                                        <option value="UTC">UTC</option>
                                        <option value="America/New_York">Eastern Time</option>
                                        <option value="America/Chicago">Central Time</option>
                                        <option value="America/Denver">Mountain Time</option>
                                        <option value="America/Los_Angeles">Pacific Time</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="format-card">
                    <div className="format-card-header">
                        <div className="format-card-title">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="4" y1="9" x2="20" y2="9" />
                                <line x1="4" y1="15" x2="20" y2="15" />
                                <line x1="10" y1="3" x2="8" y2="21" />
                                <line x1="16" y1="3" x2="14" y2="21" />
                            </svg>
                            <Tooltip content="Configure how numeric values should be displayed.">
                                <span>Number Formatting</span>
                            </Tooltip>
                        </div>
                        <label className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={formData.config_data.standardization.numbers.enabled}
                                onChange={(e) => updateFormData('config_data.standardization.numbers.enabled', e.target.checked)}
                            />
                            <span className="toggle-slider"></span>
                        </label>
                    </div>

                    {formData.config_data.standardization.numbers.enabled && (
                        <div className="format-card-content">
                            <div className="form-row">
                                <div className="form-group">
                                    <Tooltip content="Number of digits to show after the decimal point.">
                                        <label>Decimal Places</label>
                                    </Tooltip>
                                    <input
                                        type="number"
                                        className="form-input"
                                        min="0"
                                        max="10"
                                        value={formData.config_data.standardization.numbers.decimal_places}
                                        onChange={(e) => updateFormData('config_data.standardization.numbers.decimal_places', parseInt(e.target.value))}
                                    />
                                </div>
                                <div className="form-group">
                                    <Tooltip content="Character used to separate thousands (e.g., 1,000 vs 1.000).">
                                        <label>Thousand Separator</label>
                                    </Tooltip>
                                    <select
                                        className="form-input"
                                        value={formData.config_data.standardization.numbers.thousand_separator}
                                        onChange={(e) => updateFormData('config_data.standardization.numbers.thousand_separator', e.target.value)}
                                    >
                                        <option value=",">Comma (1,000)</option>
                                        <option value=".">Period (1.000)</option>
                                        <option value=" ">Space (1 000)</option>
                                        <option value="">None (1000)</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="format-card">
                    <div className="format-card-header">
                        <div className="format-card-title">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="12" y1="1" x2="12" y2="23" />
                                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                            </svg>
                            <Tooltip content="Apply currency symbols and formatting to monetary values.">
                                <span>Currency Formatting</span>
                            </Tooltip>
                        </div>
                        <label className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={formData.config_data.standardization.currency.enabled}
                                onChange={(e) => updateFormData('config_data.standardization.currency.enabled', e.target.checked)}
                            />
                            <span className="toggle-slider"></span>
                        </label>
                    </div>

                    {formData.config_data.standardization.currency.enabled && (
                        <div className="format-card-content">
                            <div className="form-row">
                                <div className="form-group">
                                    <Tooltip content="The symbol to use for currency (e.g., $, €, £).">
                                        <label>Currency Symbol</label>
                                    </Tooltip>
                                    <input
                                        type="text"
                                        className="form-input"
                                        value={formData.config_data.standardization.currency.symbol}
                                        onChange={(e) => updateFormData('config_data.standardization.currency.symbol', e.target.value)}
                                        placeholder="$"
                                    />
                                </div>
                                <div className="form-group">
                                    <Tooltip content="Whether the symbol appears before or after the number.">
                                        <label>Symbol Position</label>
                                    </Tooltip>
                                    <select
                                        className="form-input"
                                        value={formData.config_data.standardization.currency.position}
                                        onChange={(e) => updateFormData('config_data.standardization.currency.position', e.target.value)}
                                    >
                                        <option value="before">Before ($100)</option>
                                        <option value="after">After (100$)</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
    const renderConditionalSection = () => (
        <div className="form-section active">
            <div className="section-header">
                <FormatIcon type="rules" size={24} />
                <div>
                    <h3>Conditional Formatting Rules</h3>
                    <p>Apply visual styling based on data values and conditions</p>
                </div>
            </div>

            <div className="conditional-rules-container">
                <div className="rules-header">
                    <h4>Active Rules ({formData.config_data.conditional_formatting.length})</h4>
                    <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => {
                            const newRule = {
                                condition: 'value > 100',
                                style: {
                                    background_color: '#e8f5e8',
                                    text_color: '#2e7d32',
                                    font_weight: 'normal'
                                },
                                description: 'New formatting rule'
                            };
                            updateFormData('config_data.conditional_formatting', [
                                ...formData.config_data.conditional_formatting,
                                newRule
                            ]);
                        }}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="12" y1="5" x2="12" y2="19" />
                            <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                        Add Rule
                    </button>
                </div>

                {formData.config_data.conditional_formatting.length === 0 ? (
                    <div className="empty-rules">
                        <FormatIcon type="rules" size={48} />
                        <h4>No Conditional Rules</h4>
                        <p>Add rules to apply visual formatting based on data conditions</p>
                    </div>
                ) : (
                    <div className="rules-list">
                        {formData.config_data.conditional_formatting.map((rule, index) => (
                            <div key={index} className="rule-card">
                                <div className="rule-header">
                                    <div className="rule-title">
                                        <FormatIcon type="rules" size={16} />
                                        <span>Rule {index + 1}</span>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-danger btn-sm"
                                        onClick={() => {
                                            const newRules = formData.config_data.conditional_formatting.filter((_, i) => i !== index);
                                            updateFormData('config_data.conditional_formatting', newRules);
                                        }}
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <polyline points="3,6 5,6 21,6" />
                                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                                            <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                        </svg>
                                    </button>
                                </div>

                                <div className="rule-content">
                                    <div className="form-group full-width">
                                        <Tooltip content="Define when this rule should apply. Select a column, choose a comparison operator, and enter the value to compare against.">
                                            <label>Condition</label>
                                        </Tooltip>
                                        <ConditionBuilder
                                            value={rule.condition}
                                            onChange={(newCondition) => {
                                                const newRules = [...formData.config_data.conditional_formatting];
                                                newRules[index].condition = newCondition;
                                                updateFormData('config_data.conditional_formatting', newRules);
                                            }}
                                            availableColumns={templateColumns.length > 0 ? templateColumns : ['value', 'Name', 'Date', 'Amount', 'Status']}
                                        />
                                    </div>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Description</label>
                                            <input
                                                type="text"
                                                className="form-input"
                                                value={rule.description}
                                                onChange={(e) => {
                                                    const newRules = [...formData.config_data.conditional_formatting];
                                                    newRules[index].description = e.target.value;
                                                    updateFormData('config_data.conditional_formatting', newRules);
                                                }}
                                                placeholder="Describe this rule"
                                            />
                                        </div>
                                    </div>

                                    <div className="style-controls">
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label>Background Color</label>
                                                <div className="color-input-group">
                                                    <input
                                                        type="color"
                                                        className="color-input"
                                                        value={rule.style.background_color}
                                                        onChange={(e) => {
                                                            const newRules = [...formData.config_data.conditional_formatting];
                                                            newRules[index].style.background_color = e.target.value;
                                                            updateFormData('config_data.conditional_formatting', newRules);
                                                        }}
                                                    />
                                                    <span className="color-value">{rule.style.background_color}</span>
                                                </div>
                                            </div>
                                            <div className="form-group">
                                                <label>Text Color</label>
                                                <div className="color-input-group">
                                                    <input
                                                        type="color"
                                                        className="color-input"
                                                        value={rule.style.text_color}
                                                        onChange={(e) => {
                                                            const newRules = [...formData.config_data.conditional_formatting];
                                                            newRules[index].style.text_color = e.target.value;
                                                            updateFormData('config_data.conditional_formatting', newRules);
                                                        }}
                                                    />
                                                    <span className="color-value">{rule.style.text_color}</span>
                                                </div>
                                            </div>
                                            <div className="form-group">
                                                <label>Font Weight</label>
                                                <select
                                                    className="form-input"
                                                    value={rule.style.font_weight}
                                                    onChange={(e) => {
                                                        const newRules = [...formData.config_data.conditional_formatting];
                                                        newRules[index].style.font_weight = e.target.value;
                                                        updateFormData('config_data.conditional_formatting', newRules);
                                                    }}
                                                >
                                                    <option value="normal">Normal</option>
                                                    <option value="bold">Bold</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div className="rule-preview">
                                            <span
                                                className="preview-sample"
                                                style={{
                                                    backgroundColor: rule.style.background_color,
                                                    color: rule.style.text_color,
                                                    fontWeight: rule.style.font_weight
                                                }}
                                            >
                                                Sample Text
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
    const renderCleanupSection = () => (
        <div className="form-section active">
            <div className="section-header">
                <FormatIcon type="cleanup" size={24} />
                <div>
                    <h3>Data Cleanup</h3>
                    <p>Configure automatic data cleanup and organization rules</p>
                </div>
            </div>

            <div className="cleanup-grid">
                <div className="format-card">
                    <div className="format-card-header">
                        <div className="format-card-title">
                            <FormatIcon type="cleanup" size={20} />
                            <Tooltip content="Clean up the data structure and optimize for readability by removing empty space.">
                                <span>Cleanup Options</span>
                            </Tooltip>
                        </div>
                        <label className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={formData.config_data.cleanup.enabled}
                                onChange={(e) => updateFormData('config_data.cleanup.enabled', e.target.checked)}
                            />
                            <span className="toggle-slider"></span>
                        </label>
                    </div>

                    {formData.config_data.cleanup.enabled && (
                        <div className="format-card-content">
                            <div className="cleanup-options">
                                <label className="checkbox-option">
                                    <input
                                        type="checkbox"
                                        checked={formData.config_data.cleanup.remove_empty_rows}
                                        onChange={(e) => updateFormData('config_data.cleanup.remove_empty_rows', e.target.checked)}
                                    />
                                    <Tooltip content="If enabled, any row where all cells are empty will be deleted.">
                                        <span className="checkbox-label">Remove empty rows</span>
                                    </Tooltip>
                                </label>

                                <label className="checkbox-option">
                                    <input
                                        type="checkbox"
                                        checked={formData.config_data.cleanup.remove_empty_columns}
                                        onChange={(e) => updateFormData('config_data.cleanup.remove_empty_columns', e.target.checked)}
                                    />
                                    <Tooltip content="If enabled, any column where all cells are empty will be deleted.">
                                        <span className="checkbox-label">Remove empty columns</span>
                                    </Tooltip>
                                </label>

                                <label className="checkbox-option">
                                    <input
                                        type="checkbox"
                                        checked={formData.config_data.cleanup.merge_duplicate_headers}
                                        onChange={(e) => updateFormData('config_data.cleanup.merge_duplicate_headers', e.target.checked)}
                                    />
                                    <span className="checkbox-label">Merge duplicate headers</span>
                                </label>

                                <label className="checkbox-option">
                                    <input
                                        type="checkbox"
                                        checked={formData.config_data.cleanup.group_similar_data}
                                        onChange={(e) => updateFormData('config_data.cleanup.group_similar_data', e.target.checked)}
                                    />
                                    <span className="checkbox-label">Group similar data</span>
                                </label>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    const renderReportsSection = () => (
        <div className="form-section active">
            <div className="section-header">
                <FormatIcon type="reports" size={24} />
                <div>
                    <h3>Report Generation</h3>
                    <p>Configure automatic report generation and summary options</p>
                </div>
            </div>

            <div className="reports-grid">
                <div className="format-card">
                    <div className="format-card-header">
                        <div className="format-card-title">
                            <FormatIcon type="reports" size={20} />
                            <span>Summary Reports</span>
                        </div>
                        <label className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={formData.config_data.reports.summary_enabled}
                                onChange={(e) => updateFormData('config_data.reports.summary_enabled', e.target.checked)}
                            />
                            <span className="toggle-slider"></span>
                        </label>
                    </div>

                    {formData.config_data.reports.summary_enabled && (
                        <div className="format-card-content">
                            <div className="report-options">
                                <label className="checkbox-option">
                                    <input
                                        type="checkbox"
                                        checked={formData.config_data.reports.executive_summary}
                                        onChange={(e) => updateFormData('config_data.reports.executive_summary', e.target.checked)}
                                    />
                                    <span className="checkbox-label">Executive summary</span>
                                </label>

                                <label className="checkbox-option">
                                    <input
                                        type="checkbox"
                                        checked={formData.config_data.reports.audit_report}
                                        onChange={(e) => updateFormData('config_data.reports.audit_report', e.target.checked)}
                                    />
                                    <span className="checkbox-label">Audit report</span>
                                </label>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
    const renderPreviewSection = () => (
        <div className="form-section active">
            <div className="section-header">
                <FormatIcon type="preview" size={24} />
                <div>
                    <h3>Preview Configuration</h3>
                    <p>Test your formatting rules on sample data</p>
                </div>
            </div>

            <div className="preview-container">
                <div className="preview-actions">
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => onPreview(templateSampleData)}
                        disabled={loading}
                    >
                        <FormatIcon type="preview" size={16} />
                        {loading ? 'Generating Preview...' : 'Generate Preview'}
                    </button>
                </div>

                {previewData && (
                    <div className="preview-results">
                        <div className="preview-comparison">
                            <div className="preview-column">
                                <DataGrid
                                    data={previewData.original}
                                    title="Original Data"
                                    emptyMessage="No original data available"
                                />
                            </div>
                            <div className="preview-arrow">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="5" y1="12" x2="19" y2="12" />
                                    <polyline points="12,5 19,12 12,19" />
                                </svg>
                            </div>
                            <div className="preview-column">
                                <DataGrid
                                    data={previewData.formatted}
                                    title="Formatted Data"
                                    emptyMessage="No formatted data available"
                                />
                            </div>
                        </div>

                        {previewData.metadata && (
                            <div className="preview-metadata">
                                <h4>Processing Information</h4>
                                <div className="metadata-grid">
                                    <div className="metadata-item">
                                        <span className="metadata-label">Rules Applied:</span>
                                        <span className="metadata-value">{previewData.metadata.appliedRules?.length || 0}</span>
                                    </div>
                                    <div className="metadata-item">
                                        <span className="metadata-label">Processing Time:</span>
                                        <span className="metadata-value">{previewData.metadata.processingTime || 0}ms</span>
                                    </div>
                                    <div className="metadata-item">
                                        <span className="metadata-label">Warnings:</span>
                                        <span className="metadata-value">{previewData.metadata.warnings?.length || 0}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );

    const renderSortingSection = () => (
        <div className="form-section active">
            <div className="section-header">
                <FormatIcon type="sort" size={24} />
                <div>
                    <h3>Sorting & Grouping</h3>
                    <p>Configure how data is sorted and grouped in the output</p>
                </div>
            </div>

            <SortingGroupingConfig
                value={formData.config_data.sorting || {}}
                onChange={(sortingConfig) => updateFormData('config_data.sorting', sortingConfig)}
                availableColumns={formData.config_data.availableColumns || ['Name', 'Date', 'Amount', 'Status', 'Category']}
            />
        </div>
    );

    const renderExportSection = () => (
        <div className="form-section active">
            <div className="section-header">
                <FormatIcon type="reports" size={24} />
                <div>
                    <h3>Export Options</h3>
                    <p>Configure how formatted data should be exported</p>
                </div>
            </div>

            <div className="standardization-grid">
                <div className="format-card">
                    <div className="format-card-header">
                        <div className="format-card-title">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                            </svg>
                            <Tooltip content="Choose the file format and structure for your exported data.">
                                <span>Export Format</span>
                            </Tooltip>
                        </div>
                    </div>
                    <div className="format-card-content">
                        <div className="form-group">
                            <Tooltip content="Target file format for the output.">
                                <label>Output Format</label>
                            </Tooltip>
                            <select
                                className="form-input"
                                value={formData.config_data.export?.format || 'xlsx'}
                                onChange={(e) => updateFormData('config_data.export.format', e.target.value)}
                            >
                                <option value="xlsx">Excel (.xlsx)</option>
                                <option value="csv">CSV (.csv)</option>
                                <option value="json">JSON (.json)</option>
                                <option value="pdf">PDF (.pdf)</option>
                            </select>
                        </div>

                        <div className="cleanup-options">
                            <label className="checkbox-option">
                                <input
                                    type="checkbox"
                                    checked={formData.config_data.export?.include_headers !== false}
                                    onChange={(e) => updateFormData('config_data.export.include_headers', e.target.checked)}
                                />
                                <Tooltip content="Include a header row with column names at the top.">
                                    <span className="checkbox-label">Include column headers</span>
                                </Tooltip>
                            </label>

                            <label className="checkbox-option">
                                <input
                                    type="checkbox"
                                    checked={formData.config_data.export?.freeze_header_row || false}
                                    onChange={(e) => updateFormData('config_data.export.freeze_header_row', e.target.checked)}
                                />
                                <Tooltip content="Keep the header row visible while scrolling down the spreadsheet.">
                                    <span className="checkbox-label">Freeze header row (Excel only)</span>
                                </Tooltip>
                            </label>

                            <label className="checkbox-option">
                                <input
                                    type="checkbox"
                                    checked={formData.config_data.export?.auto_column_width || false}
                                    onChange={(e) => updateFormData('config_data.export.auto_column_width', e.target.checked)}
                                />
                                <Tooltip content="Automatically adjust column widths to fit the content.">
                                    <span className="checkbox-label">Auto-fit column widths</span>
                                </Tooltip>
                            </label>

                            <label className="checkbox-option">
                                <input
                                    type="checkbox"
                                    checked={formData.config_data.export?.add_filters || false}
                                    onChange={(e) => updateFormData('config_data.export.add_filters', e.target.checked)}
                                />
                                <Tooltip content="Add filter dropdowns to the header row in Excel.">
                                    <span className="checkbox-label">Add filter dropdowns (Excel only)</span>
                                </Tooltip>
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
    const renderCurrentSection = () => {
        switch (activeSection) {
            case 'basic':
                return renderBasicSection();
            case 'standardization':
                return renderStandardizationSection();
            case 'sorting':
                return renderSortingSection();
            case 'conditional':
                return renderConditionalSection();
            case 'cleanup':
                return renderCleanupSection();
            case 'export':
                return renderExportSection();
            case 'reports':
                return renderReportsSection();
            case 'preview':
                return renderPreviewSection();
            default:
                return renderBasicSection();
        }
    };

    return (
        <div className="enhanced-form">
            <div className="form-header">
                <div className="form-title">
                    <h2>Create Formatting Configuration</h2>
                    <p>Build powerful formatting rules to transform your data output</p>
                </div>
                <button
                    className="btn btn-secondary"
                    onClick={onCancel}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                    Cancel
                </button>
            </div>

            <div className="form-layout">
                <div className="form-navigation">
                    <div className="nav-header">
                        <h3>Configuration Sections</h3>
                    </div>
                    <ul className="nav-sections">
                        {sections.map((section) => {
                            const isLocked = section.id !== 'basic' && !isBasicInfoComplete;
                            return (
                                <li key={section.id}>
                                    <Tooltip
                                        content={isLocked ? "Complete Template and Configuration Name first to unlock this section" : ""}
                                        position="right"
                                    >
                                        <button
                                            type="button"
                                            className={`nav-section ${activeSection === section.id ? 'active' : ''} ${isLocked ? 'locked' : ''}`}
                                            onClick={() => !isLocked && setActiveSection(section.id)}
                                            disabled={isLocked}
                                        >
                                            <div className="nav-label">
                                                {section.label}
                                                {isLocked && (
                                                    <svg className="lock-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                                    </svg>
                                                )}
                                            </div>
                                        </button>
                                    </Tooltip>
                                </li>
                            );
                        })}
                    </ul>
                </div>

                <div className="form-content">
                    <form onSubmit={onSubmit}>
                        {renderCurrentSection()}

                        <div className="form-actions">
                            <div className="action-buttons">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={onCancel}
                                >
                                    Cancel
                                </button>
                                <Tooltip content={!isBasicInfoComplete ? "Complete Basic Info to enable preview" : ""}>
                                    <button
                                        id="tutorial-preview-btn"
                                        type="button"
                                        className="btn btn-outline"
                                        onClick={() => onPreview(templateSampleData)}
                                        disabled={loading || !isBasicInfoComplete}
                                    >
                                        <FormatIcon type="preview" size={16} />
                                        Preview
                                    </button>
                                </Tooltip>
                                <button
                                    id="tutorial-save-btn"
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={loading}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="9,11 12,14 22,4" />
                                        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                                    </svg>
                                    {loading ? 'Creating...' : 'Create Configuration'}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default EnhancedFormatForm;